import { useQuery } from 'react-query';
import { CallGroupApi } from '@frontend/api-call-group';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { DropdownField, FormFieldActionTypes, SkeletonLoader, useForm } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { DialOptionInstruction } from '../types';
import { ReadOnlyInstructionField } from './read-only-instruction';
import { InstructionFieldStyles } from './styles';

export const CallGroupInstructionField = ({
  onChange,
  instructionState,
  isReadOnly,
  disabled = false,
}: {
  onChange: (payload: DialOptionInstruction) => void;
  instructionState: DialOptionInstruction;
  isReadOnly?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const location = accessibleLocationData[selectedLocationIds[0]];
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';

  const { data: callGroups = [], isLoading } = useQuery({
    queryKey: [queryKeys.callGroups(), location.id],
    queryFn: () => CallGroupApi.listCallGroups({ tenantId: tenantId }),
    select: (response) => {
      return (response.callGroups ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((cg) => ({
          value: cg.id,
          label: cg.name,
        }));
    },
    staleTime: 10000,
    enabled: !!tenantId && !!location.id && !isReadOnly,
  });

  const form = useForm({
    fields: {
      callGroupId: {
        type: 'dropdown',
        value: instructionState.id,
      },
    },
    fieldStateReducer: (state, action) => {
      // When any field is updated, extract all field values and notify the parent component
      if (action.type === FormFieldActionTypes.Update) {
        const payload = {
          ...instructionState,
          id: state.callGroupId.value,
        };
        onChange(payload);
      }

      return state;
    },
  });

  if (isLoading) {
    return <SkeletonLoader height={40} width={225} />;
  }

  if (isReadOnly) {
    return (
      <ReadOnlyInstructionField
        instructionName={instructionState.name ?? t('Unknown Call Group')}
        iconName={'call-group'}
        instructionType={t('Call Group')}
      />
    );
  }

  return (
    <DropdownField
      {...form.getFieldProps('callGroupId')}
      name={'callGroupId'}
      label={t('Call Group')}
      css={InstructionFieldStyles}
      disabled={disabled}
    >
      {callGroups?.map((callGroup) => (
        <DropdownField.Option key={callGroup.value} value={callGroup.value}>
          {callGroup.label}
        </DropdownField.Option>
      ))}
    </DropdownField>
  );
};
